/**
 * Hesap türü (kayıt/SSO onboarding seçimi). Sunucuya giden kayıt gövdeleri DEĞİŞMEZ; seçim yalnız
 * tarayıcıda bekletilir ve ilk girişte AccountModeService tarafından POST /v2/Partners'a çevrilir.
 * Kayıt e-postaya (ve kullanıcı adına) bağlıdır: başka bir hesap aynı tarayıcıda giriş yapsa bile
 * yalnız kendi kimliğiyle eşleşen kaydı görür.
 */
export type AccountType = 'corporate' | 'organization';

export interface PendingAccountType {
    type: 'organization';
    name: string;
}

export const PENDING_ACCOUNT_TYPE_PREFIX = 'kx.pendingAccountType.';

/** Kimlik (e-posta / kullanıcı adı) normal biçimi: kırpılmış, küçük harf; boşsa null. */
export function normalizeIdentity(value: string | null | undefined): string | null {
    const v = (value ?? '').trim().toLowerCase();
    return v.length > 0 ? v : null;
}

function uniqueIdentities(identities: (string | null | undefined)[]): string[] {
    const out: string[] = [];
    for (const raw of identities) {
        const id = normalizeIdentity(raw);
        if (id && !out.includes(id)) out.push(id);
    }
    return out;
}

/** JWT'deki `email` ve `preferred_username` (AuthService.getUsername ile aynı decode deseni). */
export function tokenIdentities(token: string | null | undefined): string[] {
    if (!token) return [];
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return [];
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as { email?: unknown; preferred_username?: unknown };
        const pick = (v: unknown): string | null => (typeof v === 'string' ? v : null);
        return uniqueIdentities([pick(payload.email), pick(payload.preferred_username)]);
    } catch {
        // Çözülemeyen token: bekleyen tür okunamaz, kurumsal akış aynen sürer.
        return [];
    }
}

function isPending(value: unknown): value is PendingAccountType {
    if (!value || typeof value !== 'object') return false;
    const v = value as { type?: unknown; name?: unknown };
    return v.type === 'organization' && typeof v.name === 'string' && v.name.trim().length > 0;
}

export function savePendingAccountType(identities: (string | null | undefined)[], entry: PendingAccountType): void {
    const value = JSON.stringify({ type: entry.type, name: entry.name.trim() });
    for (const id of uniqueIdentities(identities)) {
        try {
            localStorage.setItem(PENDING_ACCOUNT_TYPE_PREFIX + id, value);
        } catch (e) {
            console.warn('Pending account type could not be stored', e);
        }
    }
}

export function readPendingAccountType(identities: (string | null | undefined)[]): PendingAccountType | null {
    for (const id of uniqueIdentities(identities)) {
        try {
            const raw = localStorage.getItem(PENDING_ACCOUNT_TYPE_PREFIX + id);
            if (!raw) continue;
            const parsed: unknown = JSON.parse(raw);
            if (isPending(parsed)) return parsed;
        } catch (e) {
            console.warn('Pending account type could not be read', e);
        }
    }
    return null;
}

export function clearPendingAccountType(identities: (string | null | undefined)[]): void {
    for (const id of uniqueIdentities(identities)) {
        try {
            localStorage.removeItem(PENDING_ACCOUNT_TYPE_PREFIX + id);
        } catch (e) {
            console.warn('Pending account type could not be cleared', e);
        }
    }
}
